/**
 * @fileoverview WEB-749 — the M2 module registry: the one place an
 * `EventModuleKind` is connected to the component that renders its body.
 *
 * ## Where this sits
 *
 * Three pieces already exist and this file invents none of them:
 *
 * - `resolveEventModules()` (WEB-700) turns a lexicon's `moduleOrder` into an
 *   ordered, self-gated `ResolvedEventModule[]`. It owns `hasContent`.
 * - `EventModuleSection` / `ModuleRowList` (WEB-707) own the section chrome
 *   and the row rhythm every module wears.
 * - `EventSpineTemplate` (WEB-704) exposes `renderModule?: (module) =>
 *   ReactNode` — a **prop**, so no module renderer ever edits the template.
 *
 * The gap between them was: nothing maps a kind to a body. That is this file,
 * and it is deliberately the *only* file the five parallel M2 module issues
 * share.
 *
 * ## Why the renderer takes a context rather than just a module
 *
 * `renderModule` on the template is `(module: ResolvedEventModule) =>
 * ReactNode` — it carries no event payload and no chroma, because the template
 * must not know how any particular module sources its rows. Renderers need
 * both. {@link createEventModuleRenderer} closes over them once at the call
 * site and hands each renderer the full {@link EventModuleProps}, which keeps
 * the template's prop type untouched and every renderer a pure function of
 * its inputs.
 *
 * ## The spacing in EVENT_MODULE_RENDERERS is load-bearing
 *
 * Five issues (WEB-710/711/712/713/714) each flip one or two entries here, in
 * parallel, on separate branches. In a dense object literal those edits land
 * on adjacent lines and every pair three-way-merges into a conflict. Each
 * entry therefore carries a comment block and a blank line, guaranteeing ≥3
 * lines of unchanged context on both sides so git merges any combination of
 * them cleanly. **Do not compact this object.**
 */

import type { ReactNode } from 'react';

import {
  EventModuleSection,
  type ModuleRowChroma,
} from '@/components/organisms/EventModuleSection';
import type { EventModuleKind, ResolvedEventModule } from '@/lib/eventDetail/modules';
import { isUsableHttpUrl } from '@/lib/ticketing';
import type { EventDetailV2 } from '@/types/eventDetail';

// Value imports for the registrations below. Each M2 module issue adds
// exactly one such import for its own component. Keep this note free of a
// per-issue list: the enumeration is what makes parallel M2 branches collide
// here, which is the whole thing this file's layout exists to avoid.
import { AboutModule } from './AboutModule';
import { GenericModule } from './GenericModule';
import { LineupModule } from './LineupModule';
import { MatchupModule } from './MatchupModule';
import { PeopleModule } from './PeopleModule';
import { SeatTierModule } from './SeatTierModule';
import { WorksModule } from './WorksModule';

/**
 * Everything an M2 module renderer is allowed to read. Every renderer in this
 * tier implements exactly this shape, which is what lets them be registered
 * interchangeably and tested identically.
 *
 * `module.hasContent` is authoritative — a renderer must not re-derive gating
 * from `event`, including row-emptiness for `generic` / `agenda` (WEB-756:
 * that check lives in `resolveGenericModuleRows`, which `hasModuleContent`
 * already reads). `resolveEventModules` states the rule once (WEB-700) and a
 * second opinion here is how the two drift apart.
 */
export interface EventModuleProps {
  /** Already-gated module from `resolveEventModules`. */
  module: ResolvedEventModule;
  /** The full event payload — the renderer's only row source. */
  event: EventDetailV2;
  /** Caller-resolved category chroma (`getCategoryChroma`). Never derived here. */
  chroma: ModuleRowChroma;
}

/** A registered module body. Returns `null` to leave the slot empty. */
export type EventModuleBodyRenderer = (props: EventModuleProps) => ReactNode;

/**
 * The shape `EventSpineTemplate`'s `renderModule` prop accepts. Declared
 * structurally rather than imported from the template so this organism takes
 * no dependency on a template; `__tests__/registry.test.tsx` asserts the two
 * stay assignable, which is what would actually catch drift.
 */
export type EventModuleSlotRenderer = (module: ResolvedEventModule) => ReactNode;

/** The `{ event, chroma }` half of {@link EventModuleProps}, bound once per page. */
export type EventModuleRenderContext = Omit<EventModuleProps, 'module'>;

/**
 * Kind → body. Exhaustive over `EventModuleKind` by type, so adding a kind to
 * the union without deciding what renders it fails type-check here rather
 * than silently rendering nothing.
 *
 * `null` is a real, honest value: the kind is ordered by some category's
 * lexicon but has no renderer yet. When `hasContent` is false, WEB-719
 * paints the degrade note from the section chrome regardless of whether
 * a body renderer is registered.
 */
export const EVENT_MODULE_RENDERERS: Readonly<
  Record<EventModuleKind, EventModuleBodyRenderer | null>
> = {
  // FRE-1313 — every category leads with this. Body copy from the event's own
  // `description`, which has shipped on the wire since API-1403 with no module
  // to render it. Rendered as text, never as markup: the copy is third-party.
  about: (props) => <AboutModule {...props} />,

  // WEB-714 — the time-led row shape. `science-tech` is the only lexicon entry
  // that orders this kind; business-networking's "Agenda" is the `generic`
  // kind under that title. That those two are one object with different words
  // is exactly why both route to the same renderer. "Time-led" is the row's
  // optional `lead` column, not list semantics — `ModuleRowList` renders a
  // real `<ul>`, never an `<ol>`.
  agenda: (props) => <GenericModule {...props} />,

  // UNOWNED in M2. `community-culture` orders this ("Who it's for") but no
  // issue in this milestone builds it, and `EventDetailV2` carries no audience
  // guidance column. Stays null; degrades through WEB-719.
  audience: null,

  // WEB-714 — the four generic-set slugs (drinks, business-networking,
  // wellness, hobbies-interests). One renderer, four lexicon vocabularies; the
  // renderer itself must contain no category-slug literal.
  generic: (props) => <GenericModule {...props} />,

  // WEB-710 — music. Artists in `performanceOrder`, billing role as the row
  // meta, no lead column (set times are not a column we have).
  lineup: (props) => <LineupModule {...props} />,

  // WEB-711 — sports. Two-sided card on `--evt-matchup`; ships degraded today
  // because no matchup source exists. Never parsed out of the event title.
  matchup: (props) => <MatchupModule {...props} />,

  // UNOWNED in M2. `food` orders this ("Menu") but no issue in this milestone
  // builds it, and there is no per-course column on the wire. Stays null;
  // degrades through WEB-719.
  menu: null,

  // WEB-713 / WEB-855 — every category, as the closing slot. One roster shape
  // whose heading the lexicon supplies (one phrase now, "Organized by"; it was
  // three per-category headings until WEB-855). Dropped entirely, before this
  // renderer is ever reached, on sources that publish no organizer.
  people: (props) => <PeopleModule {...props} />,

  // WEB-712 — art. The seven-cell week strip. Note it is ALSO mounted into the
  // decision panel's `children` slot; this entry is its editorial-column slot.
  runOfDates: null,

  // WEB-711 — sports. Tier rows with a trailing price, or the seat-map link
  // when that is the only real value we hold.
  seatTiers: (props) => <SeatTierModule {...props} />,

  // WEB-712 — art. The works-on-view checklist, thumb rows at `--evt-thumb-w`.
  // Gates off unconditionally today (no backing checklist field on
  // EventDetailV2) — see WorksModule.tsx's file header.
  works: (props) => <WorksModule {...props} />,
};

/**
 * Binds `{ event, chroma }` and returns the function `EventSpineTemplate`
 * wants for `renderModule`.
 *
 * Unregistered kinds return `null` rather than throwing: a category whose
 * lexicon orders a module we have not built yet is a normal, expected state
 * during M2's fan-out, not an error worth crashing a page over.
 *
 * @param context - The `{ event, chroma }` every renderer receives.
 * @param renderers - Injection seam, defaulted to {@link EVENT_MODULE_RENDERERS}.
 *   Production callers never pass it. It exists because every entry in the real
 *   map is `null` until the module issues land, so a test written against the
 *   real map could only ever assert "returns null" — it would pass just as
 *   happily if this function returned `null` unconditionally. Passing a stub map
 *   is what makes the dispatch assertion able to fail.
 */
export function createEventModuleRenderer(
  context: EventModuleRenderContext,
  renderers: Readonly<
    Record<EventModuleKind, EventModuleBodyRenderer | null>
  > = EVENT_MODULE_RENDERERS,
): EventModuleSlotRenderer {
  return function renderEventModule(module: ResolvedEventModule): ReactNode {
    // WEB-719 — the flip lives in the section chrome, not in each renderer.
    // A gated-off module still occupies its ordinal slot; unregistered kinds
    // (audience / menu / runOfDates today) take the same path so the page
    // shape does not change per event.
    if (!module.hasContent) {
      return (
        <EventModuleSection
          module={module}
          chroma={context.chroma}
          hasTicketLink={isUsableHttpUrl(context.event.ctaHref)}
        />
      );
    }
    const render = renderers[module.kind];
    if (!render) {
      return null;
    }
    return render({ ...context, module });
  };
}
